import { type Theme, useTheme } from '@emotion/react';
import cn from 'classnames';
import { noop } from 'lodash';
import {
  cloneElement,
  type MemoExoticComponent,
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { type To, useNavigate } from 'react-router-dom';

import type { AvatarShape } from '@didaskill/types/web';
import { Avatar, removeButtonAppearance, Tooltip, Typography } from '@didaskill/ui/design-system';
import { FormattedRelativeTimeFromNow } from '@didaskill/ui/utils';
import { openLinkInNewTab } from '@didaskill/utils/web';

import { NotificationListItemFormattedMessage } from '../formatted-message/NotificationListItemFormattedMessage';
import { ICON_HUES } from './constants';
import messages from './NotificationListItemRenderer.messages';
import * as styles from './NotificationListItemRenderer.styles';

const ICON_HUES_MAPPING = (theme: Theme): Record<ICON_HUES, [string, string]> => ({
  [ICON_HUES.BLUE]: [theme.hues.blue[500], theme.hues.blue[25]],
  [ICON_HUES.ORANGE]: [theme.hues.orange[500], theme.hues.orange[25]],
  [ICON_HUES.RED]: [theme.hues.red[500], theme.hues.red[25]],
  [ICON_HUES.YELLOW]: [theme.hues.yellow[500], theme.hues.yellow[25]],
  [ICON_HUES.GREEN]: [theme.hues.green[500], theme.hues.green[25]],
  [ICON_HUES.PURPLE]: [theme.hues.purple[500], theme.hues.purple[25]],
});

const now = new Date().toISOString();

export type NotificationListItemRendererProps = {
  /** User displayed on the notification. */
  user?: AvatarShape;
  /** Notification text. */
  content?: ReactNode;
  /** Is the notification seen. */
  isSeen?: boolean;
  /** Is the notification read. */
  isRead?: boolean;
  /** Notification creation date. */
  createdAt?: string;
  /** Icon from @icon-park. */
  icon?: ReactElement;
  /** Color of the icon. */
  iconHue?: ICON_HUES;
  /** Redirection if any. */
  href?: To;
  /** Trigger onclick if any. */
  onClick?: () => void;
  /** Is the href external to the app (e.g. Google Meets url). */
  isExternalHref?: boolean;
  /** Callback when marking as read. */
  onMarkAsRead?: () => void;
  /** Callback when marking as unread. */
  onMarkAsUnread?: () => void;
  /** Closes the list of notifications. */
  onCloseNotifications?: () => void;
};

export const NotificationListItemRendererBase = ({
  user = null,
  content = null,
  isSeen = false,
  isRead = false,
  createdAt = now,
  icon,
  iconHue = ICON_HUES.BLUE,
  href = null,
  onClick = null,
  isExternalHref = false,
  onMarkAsRead = noop,
  onMarkAsUnread = noop,
  onCloseNotifications = noop,
}: NotificationListItemRendererProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();

  const iconColors = useMemo(() => ICON_HUES_MAPPING(theme)[iconHue], [theme, iconHue]);

  const onGoToLink = useCallback(() => {
    if (!isRead) {
      onMarkAsRead();
    }

    if (href) {
      if (isExternalHref) {
        openLinkInNewTab(href as string);
      } else {
        navigate(href);
      }
    } else {
      onClick();
    }

    onCloseNotifications();
  }, [navigate, href, isExternalHref, isRead, onMarkAsRead, onCloseNotifications, onClick]);

  return (
    <div css={styles.container} className={cn({ isRead, isSeen })}>
      <button
        type="button"
        onClick={onGoToLink}
        disabled={!href && !onClick}
        css={[removeButtonAppearance, styles.link]}
        aria-label={intl.formatMessage(messages.GO_TO_LINK)}
      >
        <div css={styles.notification}>
          <div aria-hidden css={styles.iconContainer} className={cn(iconHue, { isRead })}>
            {cloneElement(icon, {
              size: 24,
              fill: iconColors,
            })}
          </div>

          {!!user && (
            <Avatar
              user={user}
              size={Avatar.SIZES.LARGE}
              className={cn({ isRead })}
              css={styles.avatar}
            />
          )}

          <div css={styles.contentContainer}>
            <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>{content}</Typography>

            <div css={styles.date} className={cn({ isRead })}>
              <Typography
                variant={
                  isRead
                    ? Typography.VARIANTS.BODY_SMALL_REGULAR
                    : Typography.VARIANTS.BODY_SMALL_BOLD
                }
              >
                <FormattedRelativeTimeFromNow date={createdAt} limitRelative={Infinity} />
              </Typography>
            </div>
          </div>
        </div>
      </button>

      <Tooltip
        content={intl.formatMessage(isRead ? messages.MARK_AS_UNREAD : messages.MARK_AS_READ)}
      >
        <button
          type="button"
          css={styles.markAsRead}
          className={cn('markAsRead', { isRead })}
          onClick={isRead ? onMarkAsUnread : onMarkAsRead}
          aria-label={intl.formatMessage(isRead ? messages.MARK_AS_UNREAD : messages.MARK_AS_READ)}
        />
      </Tooltip>
    </div>
  );
};

NotificationListItemRendererBase.displayName = 'NotificationListItemRenderer';

export const NotificationListItemRenderer: MemoExoticComponent<
  typeof NotificationListItemRendererBase
> & {
  ICON_HUES?: typeof ICON_HUES;
  FormattedMessage?: typeof NotificationListItemFormattedMessage;
} = memo(NotificationListItemRendererBase);

NotificationListItemRenderer.ICON_HUES = ICON_HUES;
NotificationListItemRenderer.FormattedMessage = NotificationListItemFormattedMessage;
